import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { motion, AnimatePresence } from 'framer-motion';
import logoWhite from '@/assets/open-insurance-logo-white.png';
import { getCurrentVersion } from '@/lib/changelog-api';
import type { SystemVersion } from '@/types/changelog';

interface AboutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AboutDialog({ open, onOpenChange }: AboutDialogProps) {
  const [version, setVersion] = useState<SystemVersion | null>(null);
  
  useEffect(() => {
    const loadVersion = async () => {
      try {
        const currentVersion = await getCurrentVersion();
        setVersion(currentVersion);
      } catch (error) {
        console.error('Failed to load version:', error);
      }
    };
    
    if (open) {
      loadVersion();
    }
  }, [open]);

  const today = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: '2-digit', 
    day: '2-digit' 
  });
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-black/95 backdrop-blur-2xl border border-white/[0.06] text-white shadow-2xl">
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="relative"
            >
              {/* Subtle gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] via-transparent to-transparent rounded-lg pointer-events-none" />
              
              <div className="relative z-10 space-y-8 py-4">
                {/* Logo - Centered, Elegant */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  className="flex justify-center"
                >
                  <img 
                    src={logoWhite} 
                    alt="Open Insurance" 
                    className="h-12 w-auto opacity-90" 
                  />
                </motion.div>

                {/* Version Info - McQueen Typography */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.6 }}
                  className="flex items-center justify-center gap-4"
                >
                  <div 
                    className="px-4 py-1.5 rounded-sm bg-gradient-to-r from-amber-900/20 to-orange-900/20 border border-amber-700/30"
                  >
                    <span 
                      className="text-[11px] font-light text-amber-300/90 uppercase"
                      style={{ letterSpacing: '0.3em' }}
                    >
                      v{version?.full_version || '1.2'}
                    </span>
                  </div>
                  <div className="h-3 w-px bg-white/10" />
                  <span 
                    className="text-[10px] font-extralight text-white/40 uppercase"
                    style={{ letterSpacing: '0.25em' }}
                  >
                    {today}
                  </span>
                </motion.div>

                {/* System Metrics - Refined Grid */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  className="bg-white/[0.02] backdrop-blur-xl border border-white/[0.06] rounded-sm p-6"
                >
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-1">
                      <div 
                        className="text-[9px] font-light text-white/30 uppercase"
                        style={{ letterSpacing: '0.25em' }}
                      >
                        Uptime
                      </div>
                      <div className="text-lg font-light text-emerald-400">
                        99.9% SLA
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div 
                        className="text-[9px] font-light text-white/30 uppercase"
                        style={{ letterSpacing: '0.25em' }}
                      >
                        APIs
                      </div>
                      <div className="text-lg font-light text-blue-400">
                        2,847
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div 
                        className="text-[9px] font-light text-white/30 uppercase"
                        style={{ letterSpacing: '0.25em' }}
                      >
                        Response
                      </div>
                      <div className="text-lg font-light text-emerald-400">
                        Sub-100ms
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div 
                        className="text-[9px] font-light text-white/30 uppercase"
                        style={{ letterSpacing: '0.25em' }}
                      >
                        Security
                      </div>
                      <div className="text-lg font-light text-white/70">
                        Enterprise
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Platform Description - Luxury Card */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.6 }}
                  className="bg-gradient-to-br from-white/[0.04] to-transparent backdrop-blur-xl border border-white/[0.08] rounded-sm p-6"
                >
                  <div className="text-center space-y-4">
                    <h3 
                      className="text-base font-light text-white/90 uppercase"
                      style={{ letterSpacing: '0.15em' }}
                    >
                      Proprietary OS
                    </h3>
                    <p className="text-sm font-extralight text-white/60 leading-relaxed max-w-md mx-auto">
                      Dedicated to delivering enterprise-level software solutions 
                      specifically designed for the insurance industry
                    </p>
                    <div className="flex justify-center gap-3 pt-2">
                      <div className="px-3 py-1 rounded-sm bg-gradient-to-r from-amber-900/15 to-orange-900/15 border border-amber-700/25">
                        <span 
                          className="text-[10px] font-light text-amber-300/80 uppercase"
                          style={{ letterSpacing: '0.2em' }}
                        >
                          Enterprise
                        </span>
                      </div>
                      <div className="px-3 py-1 rounded-sm bg-gradient-to-r from-blue-900/15 to-cyan-900/15 border border-blue-700/25">
                        <span 
                          className="text-[10px] font-light text-blue-300/80 uppercase"
                          style={{ letterSpacing: '0.2em' }}
                        >
                          Cloud
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Copyright - Whisper */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6, duration: 0.8 }}
                  className="text-center"
                >
                  <p 
                    className="text-[9px] font-extralight text-white/20 uppercase"
                    style={{ letterSpacing: '0.3em' }}
                  >
                    © 2025 Open Insurance
                  </p>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
